import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import api from '../../services/api';

/**
 * Toda a entidade com serviço activo tem contrato (Linda, 20/10/2026). Mostra primeiro a
 * simulação — que contratos se criam e com que taxa — e só cria quando se confirma.
 * Cada contrato nasce com a taxa que já se aplicava (tarifário ou omissão do serviço).
 */
interface Item { entidadeId: string; servico: string; contractId: string; rateBps: number; origem: 'TARIFARIO' | 'OMISSAO' }
interface Resposta {
  simulacao: boolean; criados: Item[];
  ambiguos: Array<{ entidadeId: string; servico: string; linhas: number }>;
  jaTinham: number; nomes: Record<string, string>;
}

export default function ContratosEmFalta({ onFechar }: { onFechar: (criou: boolean) => void }) {
  const [dados, setDados] = useState<Resposta | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aCorrer, setACorrer] = useState(false);

  useEffect(() => {
    api.get('/pass/contracts-em-falta')
      .then(({ data }) => setDados(data))
      .catch((e) => setErro(e?.response?.data?.error || e.message));
  }, []);

  const criar = async () => {
    setACorrer(true); setErro(null);
    try { const { data } = await api.post('/pass/contracts-em-falta'); setDados(data); }
    catch (e: any) { setErro(e?.response?.data?.error || e.message); }
    finally { setACorrer(false); }
  };

  const linhas = useMemo(() => [...(dados?.criados ?? [])].sort((a, b) =>
    a.servico.localeCompare(b.servico) || (dados?.nomes[a.entidadeId] ?? '').localeCompare(dados?.nomes[b.entidadeId] ?? '', 'pt')),
  [dados]);

  return (
    <Dialog open onClose={() => !aCorrer && onFechar(!!dados && !dados.simulacao)} maxWidth="md" fullWidth>
      <DialogTitle>Contratos em falta</DialogTitle>
      <DialogContent dividers>
        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
        {!dados && !erro && <CircularProgress size={24} />}
        {dados && (
          <>
            <Alert severity={dados.simulacao ? 'info' : 'success'} sx={{ mb: 2 }}>
              {dados.simulacao
                ? <>Simulação: criam-se <b>{dados.criados.length}</b> contratos ({dados.jaTinham} entidade(s)/serviço(s) já têm).
                    Cada um nasce com a taxa que já se aplicava — o que se cobra não muda.</>
                : <>Criados <b>{dados.criados.length}</b> contratos, já em vigor.</>}
            </Alert>
            {dados.ambiguos.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Ficam de fora, para decidir à mão (mais do que uma linha activa do mesmo serviço):{' '}
                {dados.ambiguos.map((a) => `${dados.nomes[a.entidadeId] ?? a.entidadeId} (${a.servico})`).join(', ')}
              </Alert>
            )}
            {linhas.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Entidade</TableCell><TableCell>Serviço</TableCell>
                    <TableCell align="right">Taxa</TableCell><TableCell>Origem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linhas.map((l) => (
                    <TableRow key={`${l.entidadeId}-${l.servico}`}>
                      <TableCell>{dados.nomes[l.entidadeId] ?? l.entidadeId}</TableCell>
                      <TableCell>{l.servico}</TableCell>
                      <TableCell align="right">{(l.rateBps / 100).toLocaleString('pt-PT')}%</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {l.origem === 'TARIFARIO' ? 'tarifário da entidade' : 'omissão do serviço'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onFechar(!!dados && !dados.simulacao)} disabled={aCorrer}>Fechar</Button>
        {dados?.simulacao && dados.criados.length > 0 && (
          <Button variant="contained" onClick={criar} disabled={aCorrer}
            startIcon={aCorrer ? <CircularProgress size={16} /> : undefined}>
            Criar {dados.criados.length} contratos
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
